import { Link } from "react-router-dom";
import { ExternalLink, Video, BookOpen, MessageCircle, ArrowRight, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

interface WhatsNextSectionProps {
  projectId: string;
}

export function WhatsNextSection({ projectId }: WhatsNextSectionProps) {
  return (
    <Card className="p-6 bg-gradient-to-br from-primary/5 to-purple-500/5 border-primary/20 mt-8">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center">
          <Sparkles className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h2 className="font-display text-lg font-semibold">What's Next?</h2>
          <p className="text-sm text-muted-foreground">Take your prompts and create stunning visuals</p>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        {/* Use in OpenArt */}
        <a
          href="https://openart.ai/home/?via=fint"
          target="_blank"
          rel="noopener noreferrer"
          className="flex flex-col items-center text-center p-4 rounded-xl bg-background/50 hover:bg-background/80 transition-colors border border-border/50 group"
        >
          <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center mb-3 group-hover:bg-purple-500/30 transition-colors">
            <ExternalLink className="w-6 h-6 text-purple-400" />
          </div>
          <h3 className="font-medium text-sm mb-1">Generate in OpenArt</h3>
          <p className="text-xs text-muted-foreground">
            Paste prompts to create AI images & videos
          </p>
        </a>

        {/* Watch Tutorial */}
        <Link
          to="/app/tutorials"
          className="flex flex-col items-center text-center p-4 rounded-xl bg-background/50 hover:bg-background/80 transition-colors border border-border/50 group"
        >
          <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center mb-3 group-hover:bg-green-500/30 transition-colors">
            <Video className="w-6 h-6 text-green-400" />
          </div>
          <h3 className="font-medium text-sm mb-1">Watch Tutorial</h3>
          <p className="text-xs text-muted-foreground">
            Learn how to use your prompts effectively
          </p>
        </Link>

        {/* Ask Fint */}
        <Link
          to="/app/askfint"
          className="flex flex-col items-center text-center p-4 rounded-xl bg-background/50 hover:bg-background/80 transition-colors border border-border/50 group"
        >
          <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center mb-3 group-hover:bg-blue-500/30 transition-colors">
            <MessageCircle className="w-6 h-6 text-blue-400" />
          </div>
          <h3 className="font-medium text-sm mb-1">Ask Fint for Help</h3>
          <p className="text-xs text-muted-foreground">
            Get personalized guidance on next steps
          </p>
        </Link>
      </div>

      {/* Monetization CTA */}
      <div className="mt-4 p-4 rounded-xl bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/20">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-green-500/20 flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <p className="font-medium text-sm">Turn this script into a product</p>
              <p className="text-xs text-muted-foreground">
                Use Monetization AI to create courses, eBooks, or Creator Swipe Files
              </p>
            </div>
          </div>
          <Button asChild variant="outline" size="sm" className="gap-1 border-green-500/30 hover:bg-green-500/10">
            <Link to="/app/monetization-ai">
              Try Monetization AI
              <ArrowRight className="w-4 h-4" />
            </Link>
          </Button>
        </div>
      </div>
    </Card>
  );
}
